import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { CheckCircle, UserIcon, Building2Icon, RefreshCw } from "lucide-react";
import type { UserType } from "@/domain/core";
import { usePostRegistrationHandler } from "@/utilities/post-registration-handler";
import { useUserContext } from "@/context/user-context";
import { config } from "@/config/environment";
import { cn, components } from "@/utilities/tailwind-utils";

// ============================================================================
// REGISTRATION SUCCESS TYPES
// ============================================================================

interface RegistrationSuccessProps {
  userType: UserType;
  onComplete?: () => void;
}

// ============================================================================
// REGISTRATION SUCCESS COMPONENT
// ============================================================================

export function RegistrationSuccess({
  userType,
  onComplete
}: RegistrationSuccessProps) {
  const [countdown, setCountdown] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [hasRefreshed, setHasRefreshed] = useState(false);

  const { handlePostRegistration } = usePostRegistrationHandler();
  const { refetch: refetchUserProfile } = useUserContext();

  // Handle post-registration token refresh and profile update (also used for retry).
  const handlePostRegistrationFlow = useCallback(async () => {
    if (hasRefreshed) return;

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      if (config.debug.enabled) {
        console.log("🔄 Starting post-registration flow...");
      }

      const result = await handlePostRegistration();

      if (result.success) {
        setHasRefreshed(true);

        if (refetchUserProfile) {
          await refetchUserProfile();
        }

        if (config.debug.enabled) {
          console.log("✅ Post-registration flow completed successfully");
        }
      } else {
        setRefreshError(result.error || "Failed to refresh user data");
        if (config.debug.enabled) {
          console.error("❌ Post-registration flow failed:", result.error);
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      setRefreshError(errorMessage);
      if (config.debug.enabled) {
        console.error("❌ Post-registration flow error:", error);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [handlePostRegistration, refetchUserProfile, hasRefreshed]);

  // Bootstrap once until success; setState only after awaits (isRefreshing starts true).
  useEffect(() => {
    if (hasRefreshed) return;

    let cancelled = false;

    void (async () => {
      try {
        if (config.debug.enabled) {
          console.log("🔄 Starting post-registration flow...");
        }

        const result = await handlePostRegistration();
        if (cancelled) return;

        if (result.success) {
          setHasRefreshed(true);
          if (refetchUserProfile) {
            await refetchUserProfile();
          }
          if (cancelled) return;
          if (config.debug.enabled) {
            console.log("✅ Post-registration flow completed successfully");
          }
        } else {
          setRefreshError(result.error || "Failed to refresh user data");
          if (config.debug.enabled) {
            console.error("❌ Post-registration flow failed:", result.error);
          }
        }
      } catch (error) {
        if (cancelled) return;
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        setRefreshError(errorMessage);
        if (config.debug.enabled) {
          console.error("❌ Post-registration flow error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasRefreshed, handlePostRegistration, refetchUserProfile]);

  // Auto-redirect countdown
  useEffect(() => {
    if (!hasRefreshed || isRefreshing || refreshError) return; // Wait for refresh to complete and no errors

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Only call onComplete if post-registration flow was successful
          if (hasRefreshed && !isRefreshing && !refreshError) {
            onComplete?.();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onComplete, hasRefreshed, isRefreshing, refreshError]);

  const getNextSteps = () => {
    if (userType === "professional") {
      return [
        "Complete your professional profile",
        "Upload your certifications and licenses",
        "Set your availability and preferences",
        "Browse and apply for available shifts",
        "Start earning with Tempii"
      ];
    } else {
      return [
        "Complete your practice profile",
        "Set up your staffing requirements",
        "Post your first job opportunity",
        "Review and hire qualified professionals",
        "Manage your staffing needs efficiently"
      ];
    }
  };

  const getIcon = () => {
    return userType === "professional" ? (
      <UserIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />
    ) : (
      <Building2Icon className="h-12 w-12 text-green-600 dark:text-green-400" />
    );
  };

  const getTitle = () => {
    return userType === "professional"
      ? "Welcome to Tempii, Healthcare Professional!"
      : "Welcome to Tempii, Healthcare Practice!";
  };

  const getSubtitle = () => {
    return userType === "professional"
      ? "Your account has been created successfully. You can now start finding and applying for healthcare opportunities."
      : "Your practice account has been created successfully. You can now start posting job opportunities and hiring qualified professionals.";
  };

  return (
    <div className="space-y-6 text-center">
      {/* Success Icon */}
      <div className="flex justify-center">
        <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full">
          <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
        </div>
      </div>

      {/* Success Message */}
      <div className="space-y-2">
        <h3 className="text-2xl font-bold">{getTitle()}</h3>
        <p className="text-muted-foreground">{getSubtitle()}</p>

        {/* User Type Badge */}
        <div className="flex justify-center mt-4">
          <Badge variant="outline" className="capitalize text-base px-4 py-2">
            {userType}
          </Badge>
        </div>
      </div>

      {/* Next Steps */}
      <div className="space-y-4">
        <h4 className="font-semibold text-lg">What's Next?</h4>
        <div className="space-y-3">
          {getNextSteps().map((step, index) => (
            <div key={index} className="flex items-center space-x-3 text-left">
              <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                {index + 1}
              </div>
              <span className="text-sm">{step}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Account Type Info */}
      <div className="p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-center space-x-3 mb-2">
          {getIcon()}
          <div className="text-left">
            <p className="font-medium">
              {userType === "professional"
                ? "Healthcare Professional"
                : "Healthcare Practice"}
            </p>
            <p className="text-sm text-muted-foreground">
              {userType === "professional"
                ? "Find opportunities and manage your career"
                : "Hire staff and manage your practice"}
            </p>
          </div>
        </div>
      </div>

      {/* Refresh Status */}
      {isRefreshing && (
        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md">
          <div className="flex items-center space-x-2">
            <LoadingSpinner size="sm" showText={false} />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Refreshing your account data...
            </p>
          </div>
        </div>
      )}

      {/* Refresh Error */}
      {refreshError && (
        <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 text-red-600 dark:text-red-400" />
            <div>
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                Failed to refresh account data
              </p>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                {refreshError}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePostRegistrationFlow}
                className="mt-2 text-xs"
              >
                <RefreshCw className="h-3 w-3" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-redirect Notice */}
      {hasRefreshed && !isRefreshing && !refreshError && (
        <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-md">
          <p className="text-sm text-green-800 dark:text-green-200">
            ✅ Account data refreshed successfully! Redirecting to your
            dashboard in {countdown} seconds...
          </p>
        </div>
      )}

      {/* Manual Continue Button */}
      <div className="pt-4">
        <Button
          onClick={onComplete}
          className={cn(components.buttonContinue, "w-full")}
          disabled={isRefreshing || !hasRefreshed || !!refreshError}
        >
          {isRefreshing ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" showText={false} />
              Refreshing Account...
            </>
          ) : refreshError ? (
            "Please fix errors above"
          ) : !hasRefreshed ? (
            "Preparing your account..."
          ) : (
            "Continue to Dashboard"
          )}
        </Button>
      </div>

      {/* Additional Information */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          You'll receive a welcome email with additional information and next
          steps.
        </p>
        <p>Need help? Contact our support team at support@tempii.ai</p>
      </div>
    </div>
  );
}
